import { useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

const CLASSIFICATIONS = ['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED', 'HIGHLY_RESTRICTED'];
const FORMATS = ['PDF', 'DOCX', 'XLSX', 'PPTX', 'TXT', 'CSV', 'ZIP', 'JPG', 'PNG', 'WEBP', 'MP4', 'MOV'];

export function M23UploadCenterPage(): ReactNode {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const [documentCode, setDocumentCode] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [classification, setClassification] = useState('INTERNAL');
  const [fileFormat, setFileFormat] = useState('PDF');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>): void {
    const picked = e.target.files?.[0] ?? null;
    setFile(picked);
    // The screen already asks for a code, a name and a format; a picked file
    // knows all three better than the user does, so fill what is still blank
    // rather than make them retype it. Anything already typed is respected.
    if (picked) {
      const base = picked.name.replace(/\.[^.]+$/, '');
      const ext = picked.name.split('.').pop()?.toUpperCase() ?? '';
      if (!documentName.trim()) setDocumentName(base);
      if (!documentCode.trim()) setDocumentCode(base.toUpperCase().replace(/[^A-Z0-9]+/g, '-').slice(0, 80));
      if (FORMATS.includes(ext)) setFileFormat(ext);
    }
  }

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setSubmitting(true);
    // A picked file goes as multipart so the bytes are actually stored; with no
    // file the request stays JSON and records metadata only, exactly as before.
    const fields: Record<string, string> = {
      documentCode,
      documentName,
      classification,
      fileFormat,
      ...(documentType ? { documentType } : {}),
      ...(user?.id ? { ownerId: user.id } : {}),
    };
    let body: unknown;
    if (file) {
      const fd = new FormData();
      for (const [k, v] of Object.entries(fields)) fd.append(k, v);
      fd.append('file', file);
      body = fd;
    } else {
      body = fields;
    }
    apiRequest<{ data: { documentCode: string } }>('/v1/documents', {
      method: 'POST',
      token,
      body,
    })
      .then((r) => {
        setMessage(t('m23.upload.msgUploaded', { code: r.data.documentCode }));
        setDocumentCode('');
        setDocumentName('');
        setFile(null);
      })
      .catch((err) => setError((err as Error).message))
      .finally(() => setSubmitting(false));
  }

  return (
    <main aria-label="upload-center">
      <h1>{t('m23.upload.title')}</h1>
      <form aria-label="upload-form" onSubmit={onSubmit}>
        <label htmlFor="documentFile">{t('m23.upload.file')}</label>
        <input id="documentFile" aria-label="document-file" type="file" onChange={onPickFile} />
        {file && <p aria-label="picked-file">{file.name} · {Math.ceil(file.size / 1024)} KB</p>}
        <label htmlFor="documentCode">{t('m23.upload.documentCode')}</label>
        <input id="documentCode" value={documentCode} onChange={(e) => setDocumentCode(e.target.value)} />
        <label htmlFor="documentName">{t('m23.upload.documentName')}</label>
        <input id="documentName" value={documentName} onChange={(e) => setDocumentName(e.target.value)} />
        <label htmlFor="documentType">{t('m23.upload.documentType')}</label>
        <input id="documentType" value={documentType} onChange={(e) => setDocumentType(e.target.value)} />
        <label htmlFor="classification">{t('m23.upload.classification')}</label>
        <select id="classification" value={classification} onChange={(e) => setClassification(e.target.value)}>
          {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <label htmlFor="fileFormat">{t('m23.upload.fileFormat')}</label>
        <select id="fileFormat" value={fileFormat} onChange={(e) => setFileFormat(e.target.value)}>
          {FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <button type="submit" disabled={submitting || !documentCode.trim() || !documentName.trim()}>{t('m23.upload.uploadDocument')}</button>
      </form>
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
    </main>
  );
}
